import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { format, startOfWeek } from 'date-fns'
import { CalendarPlus, Clock, Layers, Lock, Pencil, Plus, Trash2 } from 'lucide-react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useSimpleToast } from '../hooks/useToast'
import { Button } from '../components/ui/button'
import { Skeleton } from '../components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import { CreateCustomExerciseDialog } from '../components/training-diary/CreateCustomExerciseDialog'
import { exercisesService } from '../services/exercises'
import { workoutsService } from '../services/workouts'
import type { Exercise } from '../types/exercise'

/**
 * The user's own private exercises: what they created, and the three things
 * they can do with each one — put it on this week's diary, edit it, delete it.
 * Deleting is a soft delete server-side, so past results keep their exercise.
 */
export default function MyExercisesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showError, showSuccess } = useSimpleToast()

  const [exercises, setExercises] = useState<Exercise[] | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<Exercise | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  useBreadcrumb(
    () => ({
      onBack: () => navigate('/training-diary'),
      segments: [
        { label: t('trainingDiary.pageTitle'), onClick: () => navigate('/training-diary') },
        { label: t('trainingDiary.customExercise.pageTitle', 'My workouts'), muted: true },
      ],
    }),
    [t, navigate]
  )

  const load = useCallback(() => {
    exercisesService
      .listMine()
      .then(setExercises)
      .catch(() => {
        setExercises([])
        showError(t('trainingDiary.customExercise.listError', 'Could not load your workouts'))
      })
  }, [showError, t])

  useEffect(() => { load() }, [load])

  const scheduleThisWeek = useCallback(
    async (exercise: Exercise) => {
      const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
      await workoutsService.create({
        scheduled_date: null,
        scheduled_week_start: weekStart,
        exercise_id: exercise.id,
      })
      showSuccess(
        t('trainingDiary.customExercise.scheduled', 'Added to this week — see "Unscheduled"')
      )
    },
    [showSuccess, t]
  )

  async function handleSchedule(exercise: Exercise) {
    setBusyId(exercise.id)
    try {
      await scheduleThisWeek(exercise)
    } catch {
      showError(t('trainingDiary.customExercise.scheduleError', 'Could not add it to the diary'))
    } finally {
      setBusyId(null)
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return
    const target = pendingDelete
    setBusyId(target.id)
    try {
      await exercisesService.destroy(target.id)
      setExercises((prev) => (prev ?? []).filter((e) => e.id !== target.id))
      setPendingDelete(null)
    } catch {
      showError(t('trainingDiary.customExercise.deleteError', 'Could not delete this workout'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex flex-col gap-4 max-w-2xl mx-auto p-4 pb-12">
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-tight">
            {t('trainingDiary.customExercise.pageTitle', 'My workouts')}
          </h1>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="size-3.5 shrink-0" />
            {t('trainingDiary.customExercise.listHint', 'Only you can see these.')}
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4 mr-1" />
          {t('trainingDiary.customExercise.newButton', 'New')}
        </Button>
      </header>

      {exercises == null && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {exercises != null && exercises.length === 0 && (
        <div className="rounded-xl border border-dashed p-8 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            {t(
              'trainingDiary.customExercise.empty',
              "You haven't created any workout yet. Build one and it shows up in your picker alongside the catalog."
            )}
          </p>
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-1" />
            {t('trainingDiary.customExercise.emptyAction', 'Create a workout')}
          </Button>
        </div>
      )}

      {exercises != null && exercises.length > 0 && (
        <ul className="flex flex-col gap-2">
          {exercises.map((exercise) => (
            <li key={exercise.id} className="rounded-xl border bg-card p-3 flex flex-col gap-2">
              <div className="flex flex-col min-w-0">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
                  {exercise.category.name}
                </span>
                <span className="text-sm font-medium truncate">{exercise.title}</span>
                <span className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1">
                    <Layers className="size-3" />
                    {t('trainingDiary.customExercise.blockCount', {
                      count: exercise.templates?.length ?? 0,
                      defaultValue: '{{count}} blocks',
                    })}
                  </span>
                  {exercise.duration_minutes != null && (
                    <span className="flex items-center gap-1">
                      <Clock className="size-3" />
                      {exercise.duration_minutes}
                      {t('common.minutesShort', 'm')}
                    </span>
                  )}
                </span>
              </div>

              <div className="flex items-center gap-1.5 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={busyId === exercise.id}
                  onClick={() => handleSchedule(exercise)}
                >
                  <CalendarPlus className="size-3.5 mr-1" />
                  {t('trainingDiary.customExercise.scheduleButton', 'To diary')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => navigate(`/training-diary/exercises/${exercise.id}/edit`)}
                >
                  <Pencil className="size-3.5 mr-1" />
                  {t('common.edit', 'Edit')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive"
                  aria-label={t('common.delete', 'Delete')}
                  onClick={() => setPendingDelete(exercise)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <CreateCustomExerciseDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSchedule={scheduleThisWeek}
      />

      <AlertDialog open={pendingDelete != null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trainingDiary.customExercise.deleteTitle', 'Delete this workout?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trainingDiary.customExercise.deleteBody',
                'It disappears from your picker. Results you already logged are kept.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
